// Cache busting: adiciona versão baseada na data de build
// Quando você faz deploy, muda a versão e navegador baixa nova imagem!
const CACHE_VERSION = import.meta.env.VITE_BUILD_TIME || Date.now();

const BRAND_LOGOS = {
  lush: {
    main: `/brands/lush/logos/logo-main.png?v=${CACHE_VERSION}`,
    icon: `/brands/lush/logos/logo-icon.png?v=${CACHE_VERSION}`,
    favicon: `/brands/lush/favicon.png?v=${CACHE_VERSION}`,
  },
  'andar-de-cima': {
    main: `/brands/andar-de-cima/logos/logo-main.png?v=${CACHE_VERSION}`,
    icon: `/brands/andar-de-cima/logos/logo-icon.png?v=${CACHE_VERSION}`,
    favicon: `/brands/andar-de-cima/favicon.png?v=${CACHE_VERSION}`,
  },
  tout: {
    main: `/brands/tout/logos/logo-main.png?v=${CACHE_VERSION}`,
    icon: `/brands/tout/logos/logo-icon.png?v=${CACHE_VERSION}`,
    favicon: `/brands/tout/favicon.png?v=${CACHE_VERSION}`,
  },
  altana: {
    main: `/brands/altana/logos/logo-main.png?v=${CACHE_VERSION}`,
    icon: `/brands/altana/logos/logo-icon.png?v=${CACHE_VERSION}`,
    favicon: `/brands/altana/favicon.png?v=${CACHE_VERSION}`,
  },
};

const DEFAULT_BRAND = {
  theme: 'default',
  name: 'LHG Motéis',
  logos: {
    main: '/assets/images/logo.svg',
    icon: '/assets/images/logo-icon.svg',
    favicon: `/brands/lhg/favicon.png?v=${CACHE_VERSION}`,
  },
};

export const applyBrandTheme = brandTheme => {
  if (!brandTheme) {
    document.body.removeAttribute('data-brand-theme');
    return;
  }

  const validThemes = ['lush', 'andar-de-cima', 'tout', 'altana'];
  if (!validThemes.includes(brandTheme)) {
    document.body.removeAttribute('data-brand-theme');
    return;
  }

  document.body.setAttribute('data-brand-theme', brandTheme);
};

export const getBrandLogo = (brandTheme, type = 'main') => {
  console.log('[BRANDING] getBrandLogo called:', { brandTheme, type });

  if (!brandTheme || !BRAND_LOGOS[brandTheme]) {
    console.log('[BRANDING] No brand theme or logo config, using default');
    return DEFAULT_BRAND.logos[type];
  }

  const logoUrl = BRAND_LOGOS[brandTheme][type];
  console.log('[BRANDING] Logo URL:', logoUrl);

  if (!logoUrl) {
    console.log('[BRANDING] No logo URL found, using default');
    return DEFAULT_BRAND.logos[type];
  }

  return logoUrl;
};

export const updateFavicon = brandTheme => {
  const faviconPath =
    brandTheme && BRAND_LOGOS[brandTheme]
      ? BRAND_LOGOS[brandTheme].favicon
      : DEFAULT_BRAND.logos.favicon;

  console.log('[BRANDING] updateFavicon called:', { brandTheme, faviconPath });

  // Remove all existing favicon links to force refresh
  const existingLinks = document.querySelectorAll("link[rel*='icon']");
  existingLinks.forEach(link => {
    console.log('[BRANDING] Removing existing favicon:', link.href);
    link.remove();
  });

  // Create new favicon link
  const link = document.createElement('link');
  link.type = 'image/png';
  link.rel = 'shortcut icon';
  link.href = faviconPath;

  document.getElementsByTagName('head')[0].appendChild(link);

  console.log('[BRANDING] Favicon updated to:', faviconPath);
};

export const getBrandName = brandTheme => {
  const brandNames = {
    lush: 'LUSH MOTEL',
    'andar-de-cima': 'ANDAR DE CIMA',
    tout: 'TOUT MOTEL',
    altana: 'ALTANA',
  };

  return brandNames[brandTheme] || DEFAULT_BRAND.name;
};

export const updatePageTitle = brandTheme => {
  if (!brandTheme) {
    // Sem inbox selecionado = título padrão da LHG
    document.title = 'Chat - LHG Motéis';
    return;
  }

  const brandName = getBrandName(brandTheme);
  document.title = `${brandName} - Chat`;
};

/**
 * 🔥 NEW: Detects brand theme based on inbox
 * This is the PRIMARY method for brand detection
 * @param {Object} inbox - Inbox object with brand_theme property
 * @returns {string|null} - Brand theme name or null
 */
export const detectBrandFromInbox = inbox => {
  if (!inbox || !inbox.brand_theme) {
    console.log('[BRANDING] No inbox or brand_theme, returning null');
    return null;
  }

  const validThemes = ['lush', 'andar-de-cima', 'tout', 'altana'];
  if (!validThemes.includes(inbox.brand_theme)) {
    console.log('[BRANDING] Invalid brand_theme:', inbox.brand_theme);
    return null;
  }

  console.log('[BRANDING] ✅ Detected brand from inbox:', inbox.brand_theme);
  return inbox.brand_theme;
};

/**
 * @deprecated Use detectBrandFromInbox() instead
 * Detects brand theme based on domain/subdomain
 * This method is deprecated and will be removed in future versions
 */
export const detectBrandFromDomain = () => {
  const hostname = window.location.hostname;

  // Check for specific brand domains
  if (hostname.includes('lushmotel')) return 'lush';
  if (hostname.includes('andardecima')) return 'andar-de-cima';
  if (hostname.includes('toutmotel')) return 'tout';

  // Legacy: check for brand subdomains (lush.*, andar.*, tout.*)
  if (hostname.includes('lush')) return 'lush';
  if (hostname.includes('andar')) return 'andar-de-cima';
  if (hostname.includes('tout')) return 'tout';

  // Return null to use account.brand_theme as fallback
  return null;
};

/**
 * @deprecated No longer used - inbox-based branding doesn't require account restrictions
 * Get allowed account ID based on domain
 */
export const getAllowedAccountId = () => {
  // This function is deprecated and always returns null
  // Kept for backward compatibility only
  return null;
};

/**
 * @deprecated No longer used - inbox-based branding doesn't require account restrictions
 * Check if user can access a specific account based on domain
 */
export const canAccessAccount = accountId => {
  // This function is deprecated and always returns true
  // Kept for backward compatibility only
  return true;
};

/**
 * 🔥 UPDATED: Initialize branding with domain detection
 * Priority: domain > account > default
 */
export const initializeBranding = account => {
  // Try domain detection first
  const domainBrand = detectBrandFromDomain();
  const brandTheme = domainBrand || account?.brand_theme;

  applyBrandTheme(brandTheme);
  updateFavicon(brandTheme);

  return brandTheme;
};

export const getAvailableBrands = () => {
  return Object.keys(BRAND_LOGOS);
};

export const isValidBrandTheme = brandTheme => {
  return getAvailableBrands().includes(brandTheme);
};

export default {
  applyBrandTheme,
  getBrandLogo,
  updateFavicon,
  getBrandName,
  detectBrandFromDomain,
  initializeBranding,
  getAvailableBrands,
  isValidBrandTheme,
};
